/**
 * <announcement-bar> — the behaviour of the announcement bar (sections/announcement-bar.liquid).
 *
 * - rotate: shows one message at a time and moves to the next every few seconds. It stops while the pointer or the
 *   focus is on the bar, while the tab is hidden, after the pause button is pressed, and altogether when the visitor
 *   prefers reduced motion. Arrows move by hand; once the visitor does, the change is announced to screen readers
 *   (while it rotates by itself it is not, so a reader is not interrupted every few seconds).
 * - marquee: the scrolling is pure CSS; the script only handles the pause button.
 * - the close button hides the bar and remembers the choice, until the messages change (the hash of the content).
 */

const STORAGE_KEY = 'nt:announcement';

class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('.abar__slide')];
    this.list = this.querySelector('[data-slides]');
    this.pauseButton = this.querySelector('[data-pause]');
    this.index = Math.max(
      0,
      this.slides.findIndex((slide) => slide.hasAttribute('data-active'))
    );
    this.paused = false;
    this.hovered = false;
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

    this.querySelector('[data-close]')?.addEventListener('click', this.onClose);
    this.pauseButton?.addEventListener('click', this.onPauseButton);

    if (this.dataset.mode === 'rotate' && this.slides.length > 1) {
      this.querySelector('[data-previous]')?.addEventListener('click', () => this.go(this.index - 1, true));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
      this.addEventListener('pointerenter', this.onEnter);
      this.addEventListener('pointerleave', this.onLeave);
      this.addEventListener('focusin', this.onEnter);
      this.addEventListener('focusout', this.onLeave);
      document.addEventListener('visibilitychange', this.update);
      this.reducedMotion.addEventListener('change', this.update);
      this.update();
    }
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.update);
    this.reducedMotion?.removeEventListener('change', this.update);
  }

  /** Starts or stops the automatic rotation according to everything that can pause it. */
  update = () => {
    const shouldRun = !this.paused && !this.hovered && !document.hidden && !this.reducedMotion.matches;
    if (shouldRun) this.start();
    else this.stop();
  };

  start() {
    if (this.timer) return;
    this.timer = setInterval(() => this.go(this.index + 1), Number(this.dataset.interval) || 5000);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }

  go(target, byHand = false) {
    const count = this.slides.length;
    this.index = (target + count) % count;

    this.slides.forEach((slide, position) => {
      const active = position === this.index;
      slide.toggleAttribute('data-active', active);
      if (active) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');
    });

    // The visitor chose to move: tell assistive technology. Automatic changes stay silent.
    this.list?.setAttribute('aria-live', byHand ? 'polite' : 'off');
    if (byHand) this.update();
  }

  onEnter = () => {
    this.hovered = true;
    this.update();
  };

  onLeave = () => {
    this.hovered = this.matches(':hover') || this.matches(':focus-within');
    this.update();
  };

  onPauseButton = () => {
    this.paused = !this.paused;
    this.toggleAttribute('data-paused', this.paused);

    const button = this.pauseButton;
    button.setAttribute('aria-pressed', String(this.paused));
    button.setAttribute('aria-label', this.paused ? button.dataset.labelPlay : button.dataset.labelPause);
    this.update();
  };

  onClose = () => {
    try {
      localStorage.setItem(STORAGE_KEY, this.dataset.hash);
    } catch {
      // Storage blocked: the bar only stays closed on this page.
    }
    this.hidden = true;
    this.stop();
  };
}

if (!customElements.get('announcement-bar')) customElements.define('announcement-bar', AnnouncementBar);
